import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import QRCode from 'qrcode'
import { Button } from '@/components/ui/button'
import { Badge, Skeleton } from '@/components/ui/field'
import { linkQuery, publicUrlFor } from '@/features/links/api'
import { formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../../../_app'

export const Route = createFileRoute('/_app/links/$linkId/poster')({
  component: PosterPage,
})

function PosterPage() {
  const { linkId } = Route.useParams()
  const { me } = AppRoute.useRouteContext()
  const { data, isLoading, isError } = useQuery(linkQuery(linkId))
  const link = data?.link
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!link || !canvasRef.current) return
    const url = publicUrlFor(link.slug)
    QRCode.toCanvas(canvasRef.current, url, {
      width: 400,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    })
  }, [link])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-8">
        <Skeleton className="h-6 w-32" />
        <div className="mx-auto flex aspect-[1/1.414] max-w-[595px] flex-col items-center justify-center gap-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-8 shadow-sm">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-64" />
          <Skeleton className="h-6 w-72" />
        </div>
      </div>
    )
  }
  if (isError || !link) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-medium">Link not found</p>
        <Link to="/links" className="mt-2 inline-block text-sm underline">
          Back to links
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="no-print flex items-center justify-between print:hidden">
        <Link
          to="/links/$linkId"
          params={{ linkId }}
          className="text-sm text-[var(--color-muted-foreground)] hover:underline"
        >
          ← Back to link
        </Link>
        <Button onClick={() => window.print()}>Print poster</Button>
      </div>

      <div className="poster mx-auto flex aspect-[1/1.414] max-w-[595px] flex-col items-center justify-center gap-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-8 text-center shadow-sm print:border-0 print:shadow-none">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight">{link.title}</h1>
          {link.description && (
            <p className="text-base text-[var(--color-muted-foreground)]">{link.description}</p>
          )}
        </div>

        <canvas ref={canvasRef} className="h-auto max-w-[320px]" />

        <div className="space-y-1">
          <p className="font-mono text-lg tracking-wide">{publicUrlFor(link.slug)}</p>
          <div className="flex items-center justify-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            {link.opensAt && <span>Opens {formatDateTime(link.opensAt)}</span>}
            {link.closesAt && (
              <>
                <span>·</span>
                <span>Closes {formatDateTime(link.closesAt)}</span>
              </>
            )}
          </div>
        </div>

        {link.presetCountries && link.presetCountries.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5">
            {link.presetCountries.map((c) => (
              <Badge key={c} variant="default">
                {c}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
